// VideoStoryHooks.js - 提取的自定义钩子

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { parseEvents } from "../TIMEBAR/parseEvents";
import { getRobotEvents } from "../TIMEBAR/robotEvents";
import { processEventsForGrouping } from "../TIMEBAR/EventGroupHelper";
import { useAppStore } from "../../store/appStore";

// 处理视频故事事件的Hook
export const useVideoStoryEvents = (
  robotId, 
  timeframe, 
  activeEventId, 
  onEventClick
) => {
  const [events, setEvents] = useState([]);
  const [eventTypes, setEventTypes] = useState({});
  const [expandedEvents, setExpandedEvents] = useState({});
  const [trackedObjects, setTrackedObjects] = useState({});
  const [storyProgress, setStoryProgress] = useState(0);
  const [storyTitle, setStoryTitle] = useState("");
  const [animatingEventId, setAnimatingEventId] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [sortMethod, setSortMethod] = useState('priority');
  
  // 获取状态从应用Store
  const contextActiveEventId = useAppStore(state => state.selection.activeEventId);
  const contextRobotId = useAppStore(state => state.selection.activeRobotId);
  const appTimeframe = useAppStore(state => state.timeframe);
  const updateVideoState = useAppStore(state => state.updateVideoState);
  
  // 使用应用的timeframe如果可用
  const effectiveTimeframe = appTimeframe || timeframe;
  
  // 保存最后激活的事件引用
  const lastActiveEventRef = useRef(null);
  
  // 添加一个ref来跟踪是否已经加载过数据
  const hasLoadedRef = useRef(false);
  const lastRobotIdRef = useRef(robotId);
  const lastTimeframeRef = useRef(effectiveTimeframe);
  
  // 基于排序方法排序事件
  const sortedEvents = useMemo(() => {
    if (!events.length) return [];
    
    return [...events].sort((a, b) => {
      if (sortMethod === 'priority') {
        // 优先级优先排序（较低数字=较高优先级）
        if ((a.priority || 3) !== (b.priority || 3)) {
          return (a.priority || 3) - (b.priority || 3);
        }
        // 同一优先级按时间排序
        return a.startTime - b.startTime;
      } else {
        // 纯时间排序
        return a.startTime - b.startTime;
      }
    });
  }, [events, sortMethod]);
  
  // 准备分组事件对象
  const prepareGroupedEvents = useCallback(() => {
    const result = {};
    
    events.forEach((event, index) => {
      const priority = event.priority || 3;
      if (!result[priority]) {
        result[priority] = [];
      }
      // 确保保存原始索引
      result[priority].push({
        ...event,
        originalIndex: event.originalIndex !== undefined ? event.originalIndex : index
      });
    });
    
    return result;
  }, [events]);
  
  // 处理事件激活
  const handleEventActivation = useCallback((eventId) => {
    if (eventId === null) return;
    
    // 查找与原始索引匹配的事件
    const eventWithOriginalIndex = events.find(e => e.originalIndex === eventId);
    const event = eventWithOriginalIndex || events[eventId];
    
    if (!event || event.startTime === undefined) return;
    
    // 更新状态
    setSelectedEvent(eventId);
    
    // 明确传递正确的robotId参数
    onEventClick(event.startTime, robotId, eventId);
    
    // 获取事件在当前排序数组中的实际索引
    const actualIndex = events.findIndex(e => 
      e.originalIndex === eventId || (eventWithOriginalIndex === null && e === event)
    );
    
    // 找到正确的DOM元素并滚动
    const eventElement = document.getElementById(`story-chapter-${actualIndex}`);
    if (eventElement) {
      // 使用 requestAnimationFrame 确保DOM已更新，但只执行一次
      let animationFrameId = requestAnimationFrame(() => {
        // 滚动到章节顶部
        eventElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
        
        // 添加高亮效果
        eventElement.classList.add('story-highlight');
        setTimeout(() => {
          eventElement.classList.remove('story-highlight');
        }, 2000);
      });
      
      // 清理函数
      return () => {
        cancelAnimationFrame(animationFrameId);
      };
    }
  }, [events, onEventClick, robotId]);
  
  // 处理事件点击
// 修改 handleEventClick 函数
const handleEventClick = useCallback((index) => {
  setSelectedEvent(index);
  const event = sortedEvents[index];
  if (!event) return;
  
  // 构建视频URL
  const timePart = effectiveTimeframe === "11AM" ? "day" : "night";
  const videoUrl = `/${timePart}/videos/video${robotId}.mp4`;
  
  // 使用与时间轴事件相同的方式更新视频状态
  if (updateVideoState) {
    updateVideoState({
      robotId,
      eventId: event.originalIndex,
      videoUrl,
      time: event.startTime,
      isPlaying: true
    }, 'timeline_event_click'); // 关键：使用与时间轴相同的事件源
  }
  
  // 调用原有的点击回调
  if (onEventClick) {
    onEventClick(event.startTime, robotId, event.originalIndex);
  }
}, [sortedEvents, onEventClick, robotId, effectiveTimeframe, updateVideoState, setSelectedEvent]);
  // 滚动逻辑放到 useEffect
  useEffect(() => {
    if (selectedEvent !== null) {
      const eventElement = document.getElementById(`story-chapter-${selectedEvent}`);
      if (eventElement) {
        let animationFrameId = requestAnimationFrame(() => {
          eventElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          eventElement.classList.add('story-highlight');
          setTimeout(() => {
            eventElement.classList.remove('story-highlight');
          }, 2000);
        });
        
        return () => {
          cancelAnimationFrame(animationFrameId);
        };
      }
    }
  }, [selectedEvent]);
  
  // 检查任何对象是否被跟踪
  const hasTrackedObjects = useCallback((eventIndex) => {
    if (!trackedObjects[eventIndex]) return false;
    return Object.values(trackedObjects[eventIndex]).some(value => value === true);
  }, [trackedObjects]);
  
  // 加载事件数据
  useEffect(() => {
    // 检查是否需要重新加载
    const shouldReload = 
      !hasLoadedRef.current || 
      lastRobotIdRef.current !== robotId || 
      lastTimeframeRef.current !== effectiveTimeframe;

    if (!shouldReload) {
      return;
    }

    console.log("Fetching events for robot story:", robotId, "timeframe:", effectiveTimeframe);
    const csvContent = getRobotEvents(robotId, effectiveTimeframe);

    if (!csvContent) {
      console.error("No event data found for robot:", robotId, "timeframe:", effectiveTimeframe);
      return;
    }
    
    // 更新refs
    hasLoadedRef.current = true;
    lastRobotIdRef.current = robotId;
    lastTimeframeRef.current = effectiveTimeframe;
    
    // 解析事件
    parseEvents(csvContent, (parsedEvents) => {
      console.log("Parsed events for story:", parsedEvents);
      
      // 处理事件
      const processedEvents = parsedEvents.map((event, index) => {
        event.originalIndex = index;
        
        // 设置置信度
        if (!event.confidence) {
          if (event.probability) {
            const prob = parseFloat(event.probability);
            if (!isNaN(prob)) {
              if (prob >= 0.7) event.confidence = "high";
              else if (prob >= 0.4) event.confidence = "medium";
              else event.confidence = "low";
            } else if (typeof event.probability === 'string') {
              event.confidence = event.probability.toLowerCase();
            } else {
              // 从事件名称提取置信度
              const match = event.event.match(/\((\d+)\)/);
              if (match && match[1]) {
                const num = parseInt(match[1], 10);
                if (num === 0) event.confidence = "high";
                else if (num === 1) event.confidence = "medium";
                else event.confidence = "low";
              } else {
                event.confidence = "medium";
              }
            }
          }
        }
        
        // 清理事件名称
        event.event = event.event.replace(/\(\d+\)/, "").trim();
        
        // 处理bboxes数据
        if (event.bboxes && Array.isArray(event.bboxes)) {
          event.bboxes = event.bboxes.map((bbox, idx) => {
            // 确保bbox有id
            if (!bbox.id && bbox.ID) bbox.id = bbox.ID;
            if (!bbox.id) bbox.id = idx;
            
            // 确保bbox有category
            if (!bbox.category && bbox.label) {
              // 尝试从label提取category (例如 "person 0.92" -> "person")
              const match = /^([a-zA-Z]+)/.exec(bbox.label);
              if (match && match[1]) {
                bbox.category = match[1].toLowerCase();
              }
            }
            
            // 如果没有category，根据索引设置默认值
            if (!bbox.category) {
              bbox.category = idx % 2 === 0 ? 'person' : 'vehicle';
            }
            
            // 确保有features对象
            if (!bbox.features) {
              bbox.features = {};
            }
            
            // 修复图像路径
            if (bbox.image_path) {
              // 如果是相对路径，确保格式正确
              if (!bbox.image_path.startsWith('http') && !bbox.image_path.startsWith('/')) {
                bbox.image_path = `/${effectiveTimeframe === "11AM" ? "day" : "night"}/images/${bbox.image_path}`;
              }
            }
            
            return bbox;
          });
        }
        
        return event;
      });
      
      // 处理事件分组
      const groupedEvents = processEventsForGrouping(processedEvents);
      console.log("Grouped events for story:", groupedEvents);

      // 创建事件类型映射
      const newEventTypes = {};
      
      // 修改排序逻辑，优先级优先，然后时间
      groupedEvents.sort((a, b) => {
        // 首先按优先级排序（较低的数字=较高的优先级）
        if ((a.priority || 3) !== (b.priority || 3)) {
          return (a.priority || 3) - (b.priority || 3);
        }
        // 对于相同优先级，按时间排序
        return a.startTime - b.startTime;
      });
      
      // 批量更新状态
      setEventTypes(newEventTypes);
      setEvents(groupedEvents);
      
      // 初始化跟踪状态 - 增强版
      const initialTrackedObjects = {};
      groupedEvents.forEach((event, index) => {
        initialTrackedObjects[index] = {};
        
        // 检查事件是否有边界框数据
        if (event.bboxes && Array.isArray(event.bboxes) && event.bboxes.length > 0) {
          // 处理每个边界框
          event.bboxes.forEach((bbox, bboxIdx) => {
            // 初始化为未跟踪状态
            initialTrackedObjects[index][`box${bboxIdx}`] = false;
          });
        } else {
          // 如果没有bboxes数据，创建默认的跟踪对象
          initialTrackedObjects[index] = { box0: false };
        }
      });
      
      setTrackedObjects(initialTrackedObjects);
      
      // 生成动态故事标题
      setStoryTitle(`Robot ${robotId}'s Video Debrief`);
      
      // 设置所有事件为展开状态默认
      const initialExpandedEvents = {};
      groupedEvents.forEach((_, index) => {
        initialExpandedEvents[index] = true;
      });
      setExpandedEvents(initialExpandedEvents);
      
      // 如果有激活事件，展开它
      if (activeEventId !== null || contextActiveEventId !== null) {
        const currentActiveId = activeEventId !== null ? activeEventId : contextActiveEventId;
        if (currentActiveId !== null) {
          lastActiveEventRef.current = currentActiveId;
          handleEventActivation(currentActiveId);
        }
      }
    });
  }, [robotId, effectiveTimeframe, activeEventId, contextActiveEventId, handleEventActivation]);

  // 检测上下文中活跃事件的变化
  useEffect(() => {
    if (contextActiveEventId !== null && 
        contextActiveEventId !== lastActiveEventRef.current && 
        events.length > 0) {
      console.log("VideoStory detecting active event change from context:", contextActiveEventId);
      lastActiveEventRef.current = contextActiveEventId;
      
      // 找到与原始索引匹配的事件
      const eventWithOriginalIndex = events.find(e => e.originalIndex === contextActiveEventId);
      
      if (eventWithOriginalIndex) {
        // 在当前排序数组中找到实际索引
        const actualIndex = events.findIndex(e => e.originalIndex === contextActiveEventId);
        
        // 使用actualIndex或contextActiveEventId来激活事件
        handleEventActivation(contextActiveEventId);
      } else if (contextActiveEventId < events.length) {
        // 如果找不到匹配的原始索引，尝试使用数组索引
        handleEventActivation(contextActiveEventId);
      }
    }
  }, [contextActiveEventId, events, handleEventActivation]);
  
  // 处理从上下文进行的机器人ID更改
  useEffect(() => {
    // 当上下文中的robotId变化且与当前不同时
    if (contextRobotId && contextRobotId !== robotId) {
      console.log(`VideoStory: Context robotId changed to ${contextRobotId}, currently showing ${robotId}`);
      
      // 获取新机器人的事件数据
      const csvContent = getRobotEvents(contextRobotId, effectiveTimeframe);
      
      if (!csvContent) {
        console.error("Robot event data not found:", contextRobotId, "timeframe:", effectiveTimeframe);
        return;
      }
      
      // 解析事件数据
      parseEvents(csvContent, (parsedEvents) => {
        // 处理事件数据
        const processedEvents = parsedEvents.map((event, index) => {
          return {
            ...event,
            originalIndex: index,
            // 清理事件名称
            event: event.event.replace(/\(\d+\)/, "").trim()
          };
        });
        
        // 分组和排序事件
        const groupedEvents = processEventsForGrouping(processedEvents);
        
        // 按优先级排序
        groupedEvents.sort((a, b) => {
          if ((a.priority || 3) !== (b.priority || 3)) {
            return (a.priority || 3) - (b.priority || 3);
          }
          return a.startTime - b.startTime;
        });
        
        // 更新状态
        setEvents(groupedEvents);
        setStoryTitle(`Robot ${contextRobotId}'s Video Debrief`);
        
        // 重置选中事件
        setSelectedEvent(null);
        
        // 重置跟踪对象状态
        const initialTrackedObjects = {};
        groupedEvents.forEach((event, index) => {
          initialTrackedObjects[index] = {};
          if (event.bboxes && Array.isArray(event.bboxes)) {
            event.bboxes.forEach((_, bboxIdx) => {
              initialTrackedObjects[index][`box${bboxIdx}`] = false;
            });
          } else {
            initialTrackedObjects[index] = { box1: false, box2: false };
          }
        });
        setTrackedObjects(initialTrackedObjects);
      }, 300, effectiveTimeframe);
    }
  }, [contextRobotId, robotId, effectiveTimeframe]);

  // 专门处理activeEventId变化的效果
  useEffect(() => {
    if (contextActiveEventId !== null && 
        contextActiveEventId !== lastActiveEventRef.current && 
        events.length > 0) {
      console.log("VideoStory detecting active event change from context:", contextActiveEventId);
      lastActiveEventRef.current = contextActiveEventId;
      
      // 找到与originalIndex匹配的事件
      const eventWithOriginalIndex = events.find(e => e.originalIndex === contextActiveEventId);
      
      // 在当前排序数组中找到事件的实际索引
      let actualIndex;
      if (eventWithOriginalIndex) {
        actualIndex = events.findIndex(e => e.originalIndex === contextActiveEventId);
      } else if (contextActiveEventId < events.length) {
        // 如果找不到匹配originalIndex的事件，直接使用eventId作为索引
        actualIndex = contextActiveEventId;
      } else {
        return; // 无效的eventId
      }
      
      // 找到对应的DOM元素
      const eventElement = document.getElementById(`story-chapter-${actualIndex}`);
      
      if (eventElement) {
        // 使用requestAnimationFrame确保DOM已更新
        requestAnimationFrame(() => {
          // 滚动到章节顶部
          eventElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
          
          // 添加高亮效果
          eventElement.classList.add('story-highlight');
          setTimeout(() => {
            eventElement.classList.remove('story-highlight');
          }, 2000);
        });
      }
      
      // 更新选中状态和故事进度
      setSelectedEvent(contextActiveEventId);
      if (events.length > 0) {
        const progress = ((actualIndex + 1) / events.length) * 100;
        setStoryProgress(progress);
      }
    }
  }, [contextActiveEventId, events]);

  // 立即更新标题当robotId变化时
  useEffect(() => {
    // 只有当contextRobotId变化时更新标题
    if (contextRobotId) {
      console.log(`VideoStory: Immediately updating title to Robot ${contextRobotId}'s Video Debrief`);
      setStoryTitle(`Robot ${contextRobotId}'s Video Debrief`);
    }
  }, [contextRobotId]);
  
  // 检查事件是否是当前活动事件
  const isActiveEvent = useCallback((index) => {
    const currentActiveId = activeEventId !== null ? activeEventId : contextActiveEventId;
    return currentActiveId === index;
  }, [activeEventId, contextActiveEventId]);
  
  // 根据eventId找到实际索引
  const findActualIndexByEventId = useCallback((eventId) => {
    // 尝试通过originalIndex找到事件
    const index = events.findIndex(e => e.originalIndex === eventId);
    if (index !== -1) return index;
    
    // 如果找不到，尝试直接使用eventId作为索引
    if (eventId >= 0 && eventId < events.length) return eventId;
    
    // 无法找到对应事件
    return -1;
  }, [events]);

  // 通过eventId查找元素
  const findElementByEventId = useCallback((eventId) => {
    const actualIndex = findActualIndexByEventId(eventId);
    if (actualIndex === -1) return null;
    
    return document.getElementById(`story-chapter-${actualIndex}`);
  }, [findActualIndexByEventId]);
  
  return {
    events,
    eventTypes,
    expandedEvents,
    trackedObjects,
    setTrackedObjects,
    storyProgress,
    storyTitle,
    animatingEventId,
    selectedEvent,
    sortMethod,
    setSortMethod,
    prepareGroupedEvents,
    handleEventActivation,
    handleEventClick,
    hasTrackedObjects,
    sortedEvents,
    isActiveEvent,
    findActualIndexByEventId,
    findElementByEventId,
    effectiveTimeframe
  };
};

// 处理对象跟踪的Hook
export const useObjectTracking = (
  events, 
  trackedObjects, 
  setTrackedObjects, 
  robotId, 
  onTrackObject, 
  eventTypes, 
  getStoryDescription
) => {
  // 切换跟踪状态
  const toggleTracking = useCallback((eventIndex, boxIndex) => {
    if (!events[eventIndex]) return;
    
    // 获取事件和它的bbox数据
    const event = events[eventIndex];
    if (!event) return;
    
    // 更新本地跟踪状态
    setTrackedObjects(prev => ({
      ...prev,
      [eventIndex]: {
        ...prev[eventIndex],
        [`box${boxIndex}`]: !prev[eventIndex][`box${boxIndex}`]
      }
    }));
    
    // 如果没有回调，提前退出
    if (!onTrackObject) return;
    
    // 准备数据
    let boxData = null;
    let category = 'object';
    let features = null;
    
    // 从事件中获取bbox数据
    if (event.bboxes && Array.isArray(event.bboxes) && event.bboxes[boxIndex]) {
      boxData = event.bboxes[boxIndex];
      category = boxData.category || 'object';
      
      // 根据类别提取特征
      if (category.toLowerCase().includes('person')) {
        features = extractPersonFeatures(boxData);
        category = 'person';
      } else if (['car', 'vehicle', 'truck', 'sedan', 'suv'].some(term => 
                 category.toLowerCase().includes(term))) {
        features = extractVehicleFeatures(boxData);
        category = 'vehicle';
      }
    } else {
      // 如果没有特定bbox数据，使用默认类别
      category = boxIndex % 2 === 0 ? 'person' : 'vehicle';
    }
    
    // 创建跟踪对象
    const trackingObject = {
      eventId: eventIndex,
      boxId: boxIndex,
      label: boxData?.label || `${category} ${boxIndex + 1}`,
      imagePath: boxData?.image_path || `/api/placeholder/300/200?text=${category}`,
      isTracking: !trackedObjects[eventIndex][`box${boxIndex}`],
      summary: event.summary || getStoryDescription(event, robotId),
      timestamp: formatTime(event.startTime),
      type: event.type || 1,
      features: features,
      category: category,
      boxData: boxData
    };
    
    // 发送跟踪数据到父组件
    onTrackObject(trackingObject);
  }, [events, trackedObjects, setTrackedObjects, onTrackObject, robotId, getStoryDescription]);
  
  return {
    toggleTracking
  };
};

// 提取人物特征
const extractPersonFeatures = (boxData) => {
  if (!boxData) return null;
  
  return {
    hatColor: boxData.hat_color || 'none',
    hairColor: boxData.hair_color || 'none',
    clothingColor: boxData.clothing_color || 'none',
    clothingType: boxData.clothing_type || 'none',
    skinColor: boxData.skin_color || 'none',
    skinFeature: boxData.skin_feature || 'none',
    pantsColor: boxData.pants_color || 'none',
    pantsType: boxData.pants_type || 'none',
    shoesColor: boxData.shoes_color || 'none',
    shoesType: boxData.shoes_type || 'none'
  };
};

// 提取车辆特征
const extractVehicleFeatures = (boxData) => {
  if (!boxData) return null;
  
  return {
    vehicleType: boxData.vehicle_type || 'none',
    vehicleColor: boxData.vehicle_color || 'none',
    vehicleMake: boxData.vehicle_make || 'none',
    vehicleModel: boxData.vehicle_model || 'none',
    vehicleYear: boxData.vehicle_year || 'none',
    vehiclePlate: boxData.vehicle_plate || 'none',
    vehicleDamage: boxData.vehicle_damage || 'none',
    vehicleModifications: boxData.vehicle_modifications || 'none'
  };
};

// 处理键盘导航的Hook
export const useKeyboardNavigation = (events, selectedEvent, handleEventActivation, onTogglePlayPause) => {
  const [showKeyboardHint, setShowKeyboardHint] = useState(false);
  
  useEffect(() => {
    // 键盘事件处理器
    const handleKeyDown = (e) => {
      // 忽略在文本字段中的输入
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      // 上/下箭头导航
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault(); // 防止页面滚动
        
        if (events.length === 0) return;
        
        const currentIndex = selectedEvent !== null ? selectedEvent : -1;
        let newIndex;
        
        if (e.key === 'ArrowUp') {
          // 上一个事件
          newIndex = currentIndex <= 0 ? events.length - 1 : currentIndex - 1;
        } else {
          // 下一个事件
          newIndex = currentIndex >= events.length - 1 ? 0 : currentIndex + 1;
        }
        
        // 激活新事件
        handleEventActivation(newIndex);
        
        // 短暂显示键盘提示
        setShowKeyboardHint(true);
        setTimeout(() => {
          setShowKeyboardHint(false);
        }, 2000);
      }
      
      // 空格键暂停/播放视频
      if (e.key === ' ' && typeof onTogglePlayPause === 'function') {
        e.preventDefault();
        onTogglePlayPause();
      }
      
      // Home键跳转到第一个事件
      if (e.key === 'Home' && events.length > 0) {
        e.preventDefault();
        handleEventActivation(0);
      }
      
      // End键跳转到最后一个事件
      if (e.key === 'End' && events.length > 0) {
        e.preventDefault();
        handleEventActivation(events.length - 1);
      }
      
      // 数字键1-9跳转到百分比位置
      if (!isNaN(parseInt(e.key)) && parseInt(e.key) >= 1 && parseInt(e.key) <= 9) {
        const percentage = parseInt(e.key) / 10;
        const index = Math.floor(events.length * percentage);
        if (index < events.length) {
          handleEventActivation(index);
        }
      }
    };
    
    // 注册事件监听器
    document.addEventListener('keydown', handleKeyDown);
    
    // 清理
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [events, selectedEvent, handleEventActivation, onTogglePlayPause]);
  
  // 初始化时显示键盘提示
  useEffect(() => {
    const hintTimer = setTimeout(() => {
      setShowKeyboardHint(true);
      // 5秒后隐藏提示
      setTimeout(() => {
        setShowKeyboardHint(false);
      }, 5000);
    }, 3000);
    
    return () => clearTimeout(hintTimer);
  }, []);
  
  return {
    showKeyboardHint
  };
};

// 工具函数
export const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
};

export const getStoryDescription = (event, robotId) => {
  if (event.summary) return event.summary;
  
  // 基于事件类型生成故事风格描述
  const eventType = Number(event.type) || 1;
  const confidence = event.confidence || "medium";
  
  // 基于事件类型和置信度创建动态描述
  const baseDescriptions = [
    "Robot detected a noteworthy situation.", 
    "This is an interesting observation.",
    "This activity was detected during monitoring.",
    "This situation needs further attention."
  ];
  
  const confidenceText = {
    "high": "with high confidence",
    "medium": "with moderate confidence",
    "low": "tentatively"
  };
  
  // 创建情感描述
  return `Robot ${robotId} ${confidenceText[confidence]} recorded this scene during patrol. ${baseDescriptions[eventType % baseDescriptions.length]}`;
};

// 创建实际时间戳
export const createStoryTimeStamp = (timeframe, startTime) => {
  // 基于timeframe确定是白天还是夜晚
  const isDaytime = timeframe === "11AM";
  
  // 从视频时间计算分钟和秒
  const minutes = Math.floor(startTime / 60);
  const seconds = Math.floor(startTime % 60);
  
  // 设置基础时间
  let hour = isDaytime ? 11 : 23; // 白天为11AM，夜晚为11PM
  let finalMinutes = minutes;
  
  // 处理分钟溢出
  hour += Math.floor(finalMinutes / 60);
  finalMinutes = finalMinutes % 60;
  
  // 格式化时间
  const formattedHour = hour > 12 ? `${hour - 12}` : `${hour}`;
  const ampm = hour >= 12 ? "PM" : "AM";
  const formattedMinutes = finalMinutes.toString().padStart(2, '0');
  const formattedSeconds = seconds.toString().padStart(2, '0');
  
  // 返回格式化时间
  return `${formattedHour}:${formattedMinutes}:${formattedSeconds} ${ampm}`;
};

// 添加到 VideoStoryHooks.js

// 根据ID查找并高亮时间轴事件
export const highlightTimelineEvent = (robotId, eventId) => {
  const timelineEvent = document.getElementById(`timeline-event-robot${robotId}-${eventId}`);
  
  if (timelineEvent) {
    // 移除所有事件的激活状态
    document.querySelectorAll('.timeline-event').forEach(el => {
      el.classList.remove('active');
      el.classList.remove('active-with-animation');
    });
    
    // 添加激活状态到当前事件
    timelineEvent.classList.add('active');
    timelineEvent.classList.add('active-with-animation');
    
    // 查找时间轴容器
    const trackContainer = timelineEvent.closest('.timeline-track');
    if (trackContainer) {
      // 计算滚动位置，使事件居中
      const eventRect = timelineEvent.getBoundingClientRect();
      const containerRect = trackContainer.getBoundingClientRect();
      const scrollLeft = timelineEvent.offsetLeft - (containerRect.width / 2) + (eventRect.width / 2);
      
      // 滚动到该位置
      trackContainer.scrollLeft = scrollLeft;
    }
    
    // 动画结束后移除动画类
    setTimeout(() => {
      timelineEvent.classList.remove('active-with-animation');
    }, 3000);
  }
};